import { tickToRate } from '@chainflip/utils/tickMath';
import { useMutation } from '@tanstack/react-query';
import { getPercentageFilled } from '@/shared/components/orders/OrderStatus';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import useTracking from '@/shared/hooks/useTracking';
import { chainflipAssetMap } from '@/shared/utils/env';
import { type RpcLimitOrder } from '@/shared/utils/orders';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useExtrinsicSDK } from './useStateChainAccount';
import { LiquidityProvisionEvents, type LiquidityProvisionTrackEvents } from '../types/track';

export const useCloseLimitOrder = (order: RpcLimitOrder) => {
  const { sendExtrinsic, toast } = useExtrinsicSDK('setLimitOrder', 'close-limit-order');
  const track = useTracking<LiquidityProvisionTrackEvents>();
  const { selectedAccount: polkadotAccount } = usePolkadot();

  const getTrackingProps = () => {
    const price = tickToRate(order.tick, order.asset).toFixed();
    const amount = TokenAmount.fromAsset(
      order.original_sell_amount,
      order.type === 'bid' ? 'Usdc' : order.asset,
    ).toFixed();
    const collectableFees = TokenAmount.fromAsset(
      order.fees_earned,
      order.type === 'ask' ? 'Usdc' : order.asset,
    ).toFixed();
    const fillPercentage = getPercentageFilled(order.sell_amount, order.original_sell_amount);

    return {
      path: window.location.pathname,
      scAccount: polkadotAccount?.idSs58 ?? '-',
      position: 'open_orders',
      type: 'limit',
      side: order.type === 'bid' ? 'Buy' : 'Sell',
      price,
      amount,
      collectableFees,
      fillPercentage,
    };
  };

  const getSuccessToastMessage = () => {
    const base = chainflipAssetMap[order.asset].symbol;
    const quote = chainflipAssetMap.Usdc.symbol;
    const amount = TokenAmount.fromAsset(
      order.original_sell_amount,
      order.type === 'bid' ? 'Usdc' : order.asset,
    ).toFormatted();
    const price = tickToRate(order.tick, order.asset).toFixed(2);

    return order.type === 'bid'
      ? `Limit order to buy ${amount} ${quote} worth of ${base} @ ${price} ${quote} closed`
      : `Limit order to sell ${amount} ${base} @ ${price} ${quote} closed`;
  };

  return useMutation({
    mutationFn: async () => {
      toast.loading('Submitting transaction to the State Chain...');
      track(LiquidityProvisionEvents.CloseLimitOrder, { props: getTrackingProps() });

      return sendExtrinsic({
        baseAsset: order.asset,
        quoteAsset: 'Usdc',
        side: order.type === 'bid' ? 'Buy' : 'Sell',
        orderId: BigInt(order.id),
        tick: order.tick,
        sellAmount: TokenAmount.fromAsset(0, order.asset),
      });
    },

    onSuccess: () => {
      const props = getTrackingProps();
      track(LiquidityProvisionEvents.CloseLimitOrderSuccess, { props });
      toast.success(getSuccessToastMessage());
    },

    onError: (err) => {
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });
};
